import React from 'react';
import { Box, Typography } from '@mui/material';

interface NumberedSectionProps {
  step?: number;
  title: string;
  description?: React.ReactNode;
  /** Rendered after the title, e.g. a count. */
  meta?: React.ReactNode;
  /** Right-aligned in the title row, e.g. a "Select all" button. */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** A numbered form step rendered as a card. */
const NumberedSection: React.FC<NumberedSectionProps> = ({ step, title, description, meta, action, children }) => (
  <Box
    component="section"
    aria-label={title}
    sx={{
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
      borderRadius: 4,
      p: { xs: 2, sm: 3 },
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minHeight: 32 }}>
        {step !== undefined && (
          <Box
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: 'brand.onContainer',
              bgcolor: 'brand.container',
            }}
          >
            {step}
          </Box>
        )}
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600 }}>
          {title}
        </Typography>
        {meta && (
          <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
            {meta}
          </Typography>
        )}
        {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
      </Box>
      {description && (
        <Typography sx={{ mt: 0.5, pl: step !== undefined ? 5 : 0, fontSize: 13, lineHeight: 1.5, color: 'text.secondary' }}>{description}</Typography>
      )}
    </Box>
    {children}
  </Box>
);

export default NumberedSection;
